import { authService } from './authService';
import { QuizResultsData } from '../types/quiz';

// IMPORTANT: Following API URL pattern - must manually add /api prefix
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Types for quiz data
export interface QuizQuestion {
  id: number;
  question_number: number;
  question_text: string;
  insight_text: string;
  answers: QuizAnswer[];
}

export interface QuizAnswer {
  id: number;
  question_id: number;
  answer_key: string;
  answer_text: string;
  display_order: number;
}

export interface QuizResponse {
  question_id: number;
  answer_id: number;
}

export interface QuizSubmission {
  responses: QuizResponse[];
  parent_name: string;
  child_name: string;
  email: string;
}

export interface QuizSubmissionResult {
  success: boolean;
  message: string;
  lead_id: number;
  results_token: string;
  profile: {
    id: number;
    name: string;
    slug: string;
  };
  total_score: number;
  dimension_scores: {
    sensory: number;
    structure: number;
    pacing: number;
    confidence: number;
    interest: number;
  };
  results_url: string;
}

export interface QuizProfile {
  id: number;
  name: string;
  slug: string;
  description: string;
  triggers: any[];
  strengths: string[];
  needs: any[];
  wont_work: string[];
  action_steps: any[];
  primary_dimension: string;
  is_active: boolean;
  display_order: number;
  resources?: any[];
}

export interface QuizResults {
  success: boolean;
  lead: {
    id: number;
    parent_name: string;
    child_name: string;
    email: string;
  };
  quiz_score: number;
  dimension_scores: {
    sensory: number;
    structure: number;
    pacing: number;
    confidence: number;
    interest: number;
  };
  profile: QuizProfile;
}

export interface QuizAnalytics {
  total_completed: number;
  profile_distribution: Array<{
    profile_name: string;
    count: number;
  }>;
  average_score: number;
  completion_by_source: Array<{
    source: string;
    total: number;
    completed: number;
  }>;
  recent_completions: Array<{
    id: number;
    parent_name: string;
    child_name: string;
    email: string;
    quiz_score: number;
    quiz_completed_at: string;
  }>;
  dimension_analytics: Array<{
    dimension: string;
    avg_percentage: number;
    count: number;
  }>;
}

export interface LeadQuizAnswerData {
  answer_id: number;
  answer_text: string;
  answer_key: string;
  answer_insight: string;
  dimension_tags: string[];
  points: number;
  selected_at: string;
}

export interface LeadQuizQuestionData {
  question_id: number;
  question_number: number;
  question_text: string;
  question_insight: string;
  answers: LeadQuizAnswerData[];
}

export interface LeadQuizResponsesData {
  success: boolean;
  lead_id: number;
  quiz_data: {
    total_score: number;
    completed_at: string;
    profile: {
      id: number;
      name: string;
      slug: string;
      tagline: string;
    } | null;
    dimension_scores: {
      scores: Record<string, number>;
      possible: Record<string, number>;
      percentages: Record<string, number>;
      top_3: string[];
    } | null;
    questions: LeadQuizQuestionData[];
  };
}

class QuizService {
  /**
   * Get all quiz questions with answers (Public - no auth)
   */
  async getQuestions(): Promise<QuizQuestion[]> {
    const response = await fetch(`${API_BASE_URL}/api/public/quiz/questions`);

    if (!response.ok) {
      throw new Error('Failed to fetch quiz questions');
    }

    const data = await response.json();
    return data.questions;
  }

  /**
   * Submit quiz responses and create lead (Public - no auth)
   */
  async submitQuiz(submission: QuizSubmission): Promise<QuizSubmissionResult> {
    const response = await fetch(`${API_BASE_URL}/api/public/quiz/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(submission)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to submit quiz');
    }

    return await response.json();
  }

  /**
   * Get quiz results by token (Public - no auth)
   */
  async getResults(token: string): Promise<QuizResultsData> {
    const response = await fetch(`${API_BASE_URL}/api/public/quiz/results/${token}`);

    if (!response.ok) {
      throw new Error('Failed to fetch quiz results');
    }

    return await response.json();
  }

  /**
   * Get quiz analytics (Admin - auth required)
   */
  async getAnalytics(): Promise<QuizAnalytics> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/quiz/analytics`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch quiz analytics');
    }

    const data = await response.json();
    return data.analytics;
  }

  /**
   * Get all learning profiles (Admin - auth required)
   */
  async getProfiles(): Promise<QuizProfile[]> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/quiz/profiles`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch quiz profiles');
    }

    const data = await response.json();
    return data.profiles;
  }

  /**
   * Update a learning profile (Admin - auth required)
   */
  async updateProfile(profileId: number, updates: Partial<QuizProfile>): Promise<QuizProfile> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/quiz/profiles/${profileId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      throw new Error('Failed to update quiz profile');
    }

    const data = await response.json();
    return data.profile;
  }

  /**
   * Get detailed quiz responses for a specific lead (Admin - auth required)
   */
  async getLeadQuizResponses(leadId: number): Promise<LeadQuizResponsesData> {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE_URL}/api/leads/${leadId}/quiz-responses`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Lead not found');
      }
      if (response.status === 400) {
        throw new Error('Lead has not completed the quiz');
      }
      throw new Error('Failed to fetch quiz responses');
    }

    return await response.json();
  }
}

export const quizService = new QuizService();
